import { EditOutlined, PlusCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { Portal } from "@src/components/portal";
import { Button, Divider, Form, Input, Modal, Radio } from "antd";
import { FormInstance } from "antd/lib";
import React from "react";

export type ModeSave = "save" | "save-as-new";
type ModalSaveProps = {
    type: "create" | "edit";
    initMode?: ModeSave;
    initValues?: {
        title: string;
        description: string;
    };
    isDisabled?: boolean;
    isOwnerConfig?: boolean;
    saveOptions: {
        onCancel?: () => void;
        onOk?: (values: { title: string; description: string }, mode: ModeSave) => Promise<void>;
    };
    onValuesChange?: (values: { title: string; description: string }) => void;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};
const FORM_FIELDS = {
    title: "title",
    description: "description",
};

export const ModalSave: React.FC<ModalSaveProps> = ({
    saveOptions,
    type,
    initMode = "save",
    isDisabled = false,
    isOwnerConfig = false,
    initValues,
    open,
    setOpen,
    onValuesChange,
}) => {
    const [mode, setMode] = React.useState<ModeSave>(initMode);
    const [form] = Form.useForm();

    const handleCancel = () => {
        setOpen(false);
        saveOptions?.onCancel?.();
    };

    const handleOk = async () => {
        form.validateFields().then((values) => {
            saveOptions?.onOk?.({ ...values }, mode);
            setOpen(false);
        });
    };

    React.useEffect(() => {
        setMode(initMode);
    }, [initMode]);
    const isCreateMode = type === "create" || !isOwnerConfig;
    const _isDisabled = isOwnerConfig && isDisabled;
    return (
        <>
            <Button
                icon={type === "create" ? <PlusCircleOutlined /> : <SaveOutlined />}
                style={_isDisabled ? {} : { background: "#FFD666", borderColor: "#FAAD14", color: "black" }}
                disabled={_isDisabled}
                type="primary"
                onClick={() => setOpen(true)}
            >
                {isCreateMode ? "Save New" : "Save"}
            </Button>
            <Modal
                destroyOnHidden
                title={type === "create" ? "Create new exploration" : "Save exploration"}
                centered
                open={open}
                onCancel={handleCancel}
                onOk={() => handleOk()}
            >
                <Divider className="my-2" />
                {!isCreateMode && (
                    <Radio.Group
                        className="flex flex-col"
                        value={mode}
                        onChange={(e) => {
                            setMode(e.target.value);
                        }}
                    >
                        <Radio value={"save"}>Update this exploration</Radio>
                        <Radio value={"save-as-new"}>Save as new exploration</Radio>
                    </Radio.Group>
                )}
                <div id="modal-save-content" />
            </Modal>
            <Portal containerId="modal-save-content" disabled={!open}>
                <ModalSaveConfigText
                    form={form}
                    containerStyle={{ display: open ? (mode === "save-as-new" ? "block" : "none") : "none" }}
                    initValues={initValues}
                    onValuesChange={onValuesChange}
                />
            </Portal>
        </>
    );
};

export const ConfigTextEditor: React.FC<{
    containerStyle?: React.CSSProperties;
    onConfirmValuesChange?: (values: { title: string; description: string }) => void;
    initValues: {
        title: string;
        description: string;
    };
}> = ({ containerStyle, onConfirmValuesChange, initValues }) => {
    const [form] = Form.useForm();
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <EditOutlined className="cursor-pointer ml-2 text-blue-500" onClick={() => setOpen(true)} />
            <Modal
                destroyOnHidden
                title="Update title"
                centered
                open={open}
                afterOpenChange={(open) => {
                    if (open) {
                        form.setFieldsValue(initValues ?? {});
                    }
                }}
                onCancel={() => {
                    setOpen(false);
                }}
                onOk={() => {
                    form.validateFields().then((values) => {
                        onConfirmValuesChange?.(values);
                        setOpen(false);
                    });
                }}
            >
                <ModalSaveConfigText form={form} containerStyle={containerStyle} />
            </Modal>
        </>
    );
};

export const ModalSaveConfigText: React.FC<{
    form: FormInstance<any>;
    containerStyle?: React.CSSProperties;
    initValues?: {
        title: string;
        description: string;
    };
    onValuesChange?: (values: { title: string; description: string }) => void;
}> = ({ form, containerStyle, initValues, onValuesChange }) => {
    const formValues = Form.useWatch([], form);
    React.useEffect(() => {
        if (!formValues) return;
        onValuesChange?.(formValues);
    }, [JSON.stringify(formValues)]);

    React.useEffect(() => {
        form.setFieldsValue(initValues ?? {});
    }, [JSON.stringify(initValues)]);
    return (
        <Form form={form} style={containerStyle}>
            <div>
                <Form.Item
                    name={FORM_FIELDS.title}
                    label={
                        <div className="w-[80px] flex justify-start">
                            <span>Title</span>
                        </div>
                    }
                    required
                    rules={[{ required: true, message: "Please enter title" }]}
                >
                    <Input placeholder="Enter title" />
                </Form.Item>
                <Form.Item
                    name={FORM_FIELDS.description}
                    label={
                        <div className="w-[88px] flex justify-start">
                            <span>Description</span>
                        </div>
                    }
                >
                    <Input placeholder="Enter description" />
                </Form.Item>
            </div>
        </Form>
    );
};
